import { commonapiService, uploadService } from "../../Services";
import { EditorInput, SelectInput, Toast } from "../../Components";
import React, { useEffect, useState, useRef } from "react";
import { getCache, setCache } from "../../Helpers/cachedata";
import { brandProfile } from "../../Services/brandProfile.service";
import {
  ContentState,
  convertFromRaw,
  convertToRaw,
  EditorState,
} from "draft-js";
import { CircleX, Plus } from "lucide-react";
import { decodeFromBase64 } from "../../Components/Constants/AppConstant";
import ModelCommon from "../../Components/ModelCommon";
import NotSubscribe from "../../Components/Base/Common/NotSubscribe";
import { storageHandler } from "../../Helpers";
import { SwitchWithLabel } from "../../Components/AppSwitchWithLabel";
import BrandProfileUploader from "./BrandProfileUploader";

export default function BrandPage(props) {
  const [errors, setErrors] = useState({});
  const [brandprofileData, setBrandprofileData] = useState([]);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [brandForms, setBrandForms] = useState({});
  const [editorStates, setEditorStates] = useState({});
  const [country, setCountry] = useState(null);
  const [toastList, setToastList] = useState([]);
  const [selectedImages, setSelectedImages] = useState({});
  const [modal, setModal] = useState(null);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [isUserHasSubscription, setIsUserHasSubscription] = useState();
  const [primaryBrands, setPrimaryBrands] = useState({});
  const [croppedImages, setCroppedImages] = useState({});
  const [uploadedImageUrls, setUploadedImageUrls] = useState({});
  const [isLoading, setIsLoading] = useState(true); // Add loading state

  const fileInputRefs = useRef({});
  const [uploaderInstanceKeys, setUploaderInstanceKeys] = useState({});

  const isSingleProfile = () => brandprofileData.length === 1;
  const [isAsset] = useState(true);
 // const [isUserHasSubscription, setIsUserHasSubscription] = useState();
 // const [primaryBrands, setPrimaryBrands] = useState({});

  const isValidUrl = (url) => {
    if (!url || url.trim() === "") return true;
    try {
      const urlObj = new URL(url.trim());
      return urlObj.protocol === "https:";
    } catch {
      return false;
    }
  };

  const validateForm = (index) => {
    const formData = brandForms[index];
    const logo_img = getImageSrc(index);
    const filteredSocialLinks = (formData.social_links || []).filter(
      (link) => link.trim() !== ""
    );
    const newErrors = {};

    // Brand name validation
    if (!formData.brand_name?.trim()) {
      newErrors.brand_name = "Please enter brand name";
    }

    // Industry validation
    if (!formData.industry?.trim()) {
      newErrors.industry = "Please enter industry name";
    }

    // Country validation
    if (!formData.country_id) {
      newErrors.country_id = "Please select country";
    }

    // Website validation
    if (!formData.website?.trim()) {
      newErrors.website = "Please enter website URL";
    } else if (!isValidUrl(formData.website)) {
      newErrors.website = "Please use https:// URLs only";
    }

    // Logo validation
    if (!logo_img) {
      newErrors.logo_img = "Please upload profile photo";
    }

    // Bio validation
    if (!formData.bio?.trim()) {
      newErrors.bio = "Please enter bio";
    }

    // Social links validation
    if (filteredSocialLinks.length === 0) {
      newErrors.social_links = "Please enter at least one social link";
    } else {
      const invalidLinks = filteredSocialLinks.filter(
        (link) => !isValidUrl(link)
      );
      if (invalidLinks.length > 0) {
        newErrors.social_links = "Please use https:// URLs only";
      }
    }

    setErrors((prev) => ({ ...prev, [index]: newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const clearFieldError = (index, field) => {
    setErrors((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: undefined,
      },
    }));
  };

  const getFieldError = (index, field) => {
    return errors[index]?.[field];
  };

  const handleCroppedImageUpload = async (index, croppedImageData) => {
    try {
      const response = await fetch(croppedImageData);
      const blob = await response.blob();

      const file = new File([blob], `brand-logo-${Date.now()}.png`, {
        type: "image/png",
      });

      const uploadResponse = await uploadService.upload(
        file,
        () => {},
        true,
        (progressEvent) => {}
      );

      let data;
      try {
        data = decodeFromBase64(uploadResponse);
      } catch (decodeError) {
        data = uploadResponse;
      }

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (parseError) {
          console.error("Failed to parse response:", parseError);
          return null;
        }
      }

      if (data && (data.message === "success" || data.success)) {
        const imageUrl =
          data.thumbnail_path || data.path || data.url || data.imageUrl;

        if (!imageUrl) {
          addToast({ error: true, msg: "No image URL received from server" });
          return null;
        }

        return imageUrl;
      } else {
        console.error("Upload failed or invalid response:", data);
        addToast({ error: true, msg: data?.message || "Upload failed" });
        return null;
      }
    } catch (error) {
      console.error("Upload error:", error);
      addToast({ error: true, msg: "Failed to upload profile photo" });
      return null;
    }
  };

  const updateForm = (index, data) => {
    setBrandForms((prevForms) => ({
      ...prevForms,
      [index]: { ...prevForms[index], ...data },
    }));
  };

  const setInput = (index, key) => (event) => {
    let value = event.target.value;

    if (key === "website") {
      // Fix double https:// by removing the duplicate
      if (value && value.includes("https://https://")) {
        value = value.replace("https://https://", "https://");
      }
      // Only add https:// if value has actual content and doesn't already have protocol
      else if (
        value &&
        value.trim() !== "" &&
        value !== "https://" &&
        value !== "http://" &&
        !value.startsWith("https://") &&
        !value.startsWith("http://")
      ) {
        value = "https://" + value;
      }
    }

    updateForm(index, { [key]: value });
    clearFieldError(index, key);
  };

  const setBio = (index, bioContent) => {
    updateForm(index, { bio: bioContent });
    clearFieldError(index, "bio");
  };

  const updateTextDescription = async (index, state) => {
    setEditorStates((prev) => ({
      ...prev,
      [index]: state,
    }));

    const contentState = state.getCurrentContent();
    const rawContent = convertToRaw(contentState);
    const jsonString = JSON.stringify(rawContent);

    if (contentState.hasText()) {
      if (brandForms[index]?.bio !== jsonString) {
        setBio(index, jsonString);
      }
    } else {
      if (brandForms[index]?.bio !== "") {
        setBio(index, "");
      }
    }
  };

  useEffect(() => {
    if (brandprofileData.length > 0) {
      const initialForms = {};
      const initialEditorStates = {};
      const initialPrimary = {};
      const initialCroppedImages = {};
      const initialUploaderKeys = {};

      brandprofileData.forEach((data, index) => {
        initialForms[index] = {
          brand_name: data.brand_name || "",
          country_id: data.country_id || "",
          website: data.website || "https://",
          bio: data.bio || "",
          logo_img: data.logo_img || "",
          logo_img_url: data.logo_img || "",
          industry: data.industry || "",
          social_links:
            data.social_links && data.social_links.length > 0
              ? data.social_links
              : ["https://"],
          primary: data.primary || 0,
          ...(data.id && { id: data.id }),
        };

        initialPrimary[index] = data.primary === 1 || data.primary === true;

        if (data.logo_img) {
          initialCroppedImages[index] = data.logo_img;
        }

        initialUploaderKeys[index] = `uploader-${index}-${Date.now()}`;

        const getInitialContentState = (bio) => {
          if (!bio) {
            return ContentState.createFromText("");
          }
          try {
            const parsedBio = JSON.parse(bio);
            if (parsedBio.blocks && Array.isArray(parsedBio.blocks)) {
              return convertFromRaw(parsedBio);
            }
            return ContentState.createFromText(
              typeof parsedBio === "string"
                ? parsedBio
                : JSON.stringify(parsedBio)
            );
          } catch (error) {
            return ContentState.createFromText(bio);
          }
        };

        const contentState = getInitialContentState(data.bio);
        initialEditorStates[index] =
          EditorState.createWithContent(contentState);
      });

      setBrandForms(initialForms);
      setEditorStates(initialEditorStates);
      setPrimaryBrands(initialPrimary);
      setCroppedImages(initialCroppedImages);
      setUploaderInstanceKeys(initialUploaderKeys);
    }
  }, [brandprofileData]);

  useEffect(() => {
    async function fetchCountry() {
      try {
        const cachedDataforCountry = getCache("country");
        if (cachedDataforCountry) {
          setCountry(cachedDataforCountry);
        } else {
          const res = await commonapiService.getCountry();
          if (res.success && res.data) {
            setCountry(res.data);
            setCache("country", res.data);
          }
        }
      } catch (err) {
        addToast({ error: true, msg: err.message });
      }
    }
    fetchCountry();
    // eslint-disable-next-line
  }, []);

  const selectCountryList = React.useMemo(() => {
    if (!country) return [];
    return country.map((country) => ({
      id: String(country.id),
      value: country.name,
      code: country.code,
    }));
  }, [country]);

  const getSelectedCountry = (index) => {
    const form = brandForms[index] || {};
    const dataCountryId = brandprofileData[index]?.country_id;

    const countryId = form.country_id || dataCountryId;

    if (!countryId || countryId === "" || countryId === null) {
      return null;
    }

    const countryIdStr = String(countryId);
    const found = selectCountryList.find(
      (item) => String(item.id) === countryIdStr
    );

    return found || null;
  };

  const addToast = (data) => {
    const newToastList = [...toastList];
    if (data.msg) {
      if (data.msg[data.msg.length - 1] === ".")
        data.msg = data.msg.slice(0, -1);
    }
    newToastList.push({ ...data, id: `${Date.now()}-${toastList.length}` });
    setToastList(newToastList);
  };

  useEffect(() => {
    fetchBrandProfileData();
    // eslint-disable-next-line
  }, []);

  const fetchBrandProfileData = async () => {
    setIsLoading(true); // Start loading
    try {
      const res = await brandProfile.getBrandProfile();
      console.log("🚀 ~ fetchBrandProfileData ~ res:", res);
      if (res.success && res.data) {
        setBrandprofileData(res.data);
      } else {
        setBrandprofileData([]);
      }
    } catch (error) {
      console.error("Error fetching brand profile:", error);
      setBrandprofileData([]);
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const handleSave = async (index, data) => {
    const formData = brandForms[index];

    if (!validateForm(index)) {
      addToast({ error: true, msg: "Please fix all validation errors" });
      return;
    }

    let logo_img = getImageSrc(index);

    if (croppedImages[index] && croppedImages[index].startsWith("data:")) {
      const uploadedUrl = await handleCroppedImageUpload(
        index,
        croppedImages[index]
      );
      if (!uploadedUrl) {
        addToast({ error: true, msg: "Failed to upload profile photo" });
        return;
      }
      logo_img = uploadedUrl;
      setCroppedImages((prev) => ({
        ...prev,
        [index]: uploadedUrl,
      }));
      setUploadedImageUrls((prev) => ({
        ...prev,
        [index]: uploadedUrl,
      }));
    } else if (
      croppedImages[index] &&
      (croppedImages[index].startsWith("http://") ||
        croppedImages[index].startsWith("https://"))
    ) {
      logo_img = croppedImages[index];
    } else if (uploadedImageUrls[index]) {
      logo_img = uploadedImageUrls[index];
    } else if (!logo_img && formData.logo_img) {
      logo_img = formData.logo_img;
    } else if (!logo_img && formData.logo_img_url) {
      logo_img = formData.logo_img_url;
    }

    if (!logo_img) {
      addToast({ error: true, msg: "Please upload a profile photo" });
      return;
    }

    const filteredSocialLinks = (formData.social_links || []).filter(
      (link) => link.trim() !== ""
    );

    const dataToSave = {
      brand_name: formData.brand_name,
      country_id: formData.country_id,
      website: formData.website || "",
      bio: formData.bio || "",
      logo_img: logo_img,
      industry: formData.industry,
      social_links: filteredSocialLinks,
      primary: primaryBrands[index] ? 1 : 0,
      ...(data.id && { id: data.id }),
    };
    console.log("🚀 ~ handleSave ~ dataToSave:", dataToSave);

    try {
      const res = await brandProfile.saveBrandProfileData(dataToSave);
      console.log("🚀 ~ handleSave ~ res:", res);

      if (res.success) {
        const successMsg = data.id
          ? "Brand updated successfully!"
          : "Brand saved successfully!";
        addToast({ error: false, msg: successMsg });
        setErrors((prev) => ({ ...prev, [index]: {} }));

        updateForm(index, {
          ...formData,
          country_id: formData.country_id,
          logo_img: logo_img,
          logo_img_url: logo_img,
          id: res.data?.id || data.id,
        });

        setCroppedImages((prev) => ({
          ...prev,
          [index]: logo_img,
        }));

        // Reset uploader instance
        setUploaderInstanceKeys((prev) => ({
          ...prev,
          [index]: `uploader-${index}-${Date.now()}`,
        }));

        // Refresh data from server
        await fetchBrandProfileData();
        setOpenAccordion(null);
      } else {
        addToast({ error: true, msg: res.message || "Failed to save brand" });
      }
    } catch (error) {
      console.error("Save error:", error);
      addToast({ error: true, msg: "Error saving brand profile" });
    }
  };

  const handleAddNew = () => {
    const isFirstProfile = brandprofileData.length === 0;
    const newIndex = 0;

    const newBrand = {
      brand_name: "New Brand",
      country_id: "",
      website: "https://",
      bio: "",
      logo_img_url: "",
      logo_img: "",
      industry: "",
      social_links: ["https://"],
      primary: isFirstProfile ? 1 : 0,
    };

    // Add to beginning of array
    setBrandprofileData((prev) => [newBrand, ...prev]);

    // Helper to shift indices up
    const shiftIndicesUp = (obj) => {
      const newObj = {};
      Object.keys(obj).forEach((key) => {
        const keyNum = parseInt(key);
        newObj[keyNum + 1] = obj[keyNum];
      });
      return newObj;
    };

    setBrandForms((prev) => ({
      [newIndex]: newBrand,
      ...shiftIndicesUp(prev),
    }));

    setEditorStates((prev) => ({
      [newIndex]: EditorState.createWithContent(
        ContentState.createFromText("")
      ),
      ...shiftIndicesUp(prev),
    }));

    setSelectedImages((prev) => ({
      [newIndex]: null,
      ...shiftIndicesUp(prev),
    }));

    setCroppedImages((prev) => ({
      [newIndex]: null,
      ...shiftIndicesUp(prev),
    }));

    setPrimaryBrands((prev) => ({
      [newIndex]: isFirstProfile,
      ...shiftIndicesUp(prev),
    }));

    setErrors((prev) => ({
      [newIndex]: {},
      ...shiftIndicesUp(prev),
    }));

    setUploaderInstanceKeys((prev) => ({
      [newIndex]: `uploader-${newIndex}-${Date.now()}`,
      ...shiftIndicesUp(prev),
    }));

    setUploadedImageUrls((prev) => ({
      [newIndex]: null,
      ...shiftIndicesUp(prev),
    }));

    fileInputRefs.current[newIndex] = React.createRef();

    setOpenAccordion(newIndex);
  };

  useEffect(() => {
    if (brandprofileData.length === 1) {
      setPrimaryBrands({ 0: true });
      setBrandForms((prev) => ({
        ...prev,
        0: {
          ...prev[0],
          primary: 1,
        },
      }));
    } else if (brandprofileData.length === 0) {
      setBrandForms({});
      setEditorStates({});
      setSelectedImages({});
      setCroppedImages({});
      setPrimaryBrands({});
      setErrors({});
      setUploaderInstanceKeys({});
      setUploadedImageUrls({});
      fileInputRefs.current = {};
      setOpenAccordion(null);
    }
  }, [brandprofileData.length]);

  const handleDelete = async (
    index,
    data,
    closeModal = false,
    setIsLoading
  ) => {
    if (setIsLoading) setIsLoading(true);
    try {
      const res = await brandProfile.deleteBrandProfile(data.id);
      if (res.success) {
        setBrandprofileData((prev) => prev.filter((_, i) => i !== index));

        const removeAndShiftDown = (obj, idxToRemove) => {
          const newObj = {};
          Object.keys(obj).forEach((key) => {
            const keyNum = parseInt(key);
            if (keyNum < idxToRemove) {
              newObj[keyNum] = obj[keyNum];
            } else if (keyNum > idxToRemove) {
              newObj[keyNum - 1] = obj[keyNum];
            }
          });
          return newObj;
        };

        setBrandForms((prev) => removeAndShiftDown(prev, index));
        setEditorStates((prev) => removeAndShiftDown(prev, index));
        setSelectedImages((prev) => removeAndShiftDown(prev, index));
        setCroppedImages((prev) => removeAndShiftDown(prev, index));
        setPrimaryBrands((prev) => removeAndShiftDown(prev, index));
        setErrors((prev) => removeAndShiftDown(prev, index));
        setUploaderInstanceKeys((prev) => removeAndShiftDown(prev, index));
        setUploadedImageUrls((prev) => removeAndShiftDown(prev, index));

        delete fileInputRefs.current[index];

        if (openAccordion === index) {
          setOpenAccordion(null);
        } else if (openAccordion > index) {
          setOpenAccordion(openAccordion - 1);
        }

        await fetchBrandProfileData();

        addToast({ error: false, msg: "Brand deleted successfully" });
      } else {
        addToast({
          error: true,
          msg: res.message || "Failed to delete brand",
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      addToast({ error: true, msg: "Error deleting brand profile" });
    } finally {
      if (setIsLoading) setIsLoading(false);
      if (closeModal) setModal(null);
    }
  };

  const getImageSrc = (index) => {
    const form = brandForms[index] || {};
    const brandData = brandprofileData[index] || {};

    const isHttpUrl = (str) => {
      return (
        str &&
        typeof str === "string" &&
        (str.startsWith("http://") || str.startsWith("https://"))
      );
    };
    // First check for uploaded image URL (http/https)
    if (uploadedImageUrls[index] && isHttpUrl(uploadedImageUrls[index])) {
      return uploadedImageUrls[index];
    }

    // Then check cropped images
    if (croppedImages[index]) {
      return croppedImages[index];
    }

    // Then check existing data from server
    if (brandData.logo_img && isHttpUrl(brandData.logo_img)) {
      return brandData.logo_img;
    }

    if (form.logo_img && isHttpUrl(form.logo_img)) {
      return form.logo_img;
    }

    if (form.logo_img_url && isHttpUrl(form.logo_img_url)) {
      return form.logo_img_url;
    }
    const isDataUrl = (str) => {
      return str && typeof str === "string" && str.startsWith("data:image/");
    };
    // Check for data URLs last
    if (croppedImages[index] && isDataUrl(croppedImages[index])) {
      return croppedImages[index];
    }

    if (selectedImages[index]) {
      return selectedImages[index];
    }

    return null;
  };

  const handleAddLink = (brandIndex) => {
    const currentLinks = brandForms[brandIndex]?.social_links || ["https://"];
    if (currentLinks.length < 4) {
      updateForm(brandIndex, {
        social_links: [...currentLinks, "https://"],
      });
    }
  };

  const handleLinkChange = (brandIndex, linkIndex, value) => {
    // Fix double https:// by removing the duplicate
    if (value && value.includes("https://https://")) {
      value = value.replace("https://https://", "https://");
    }
    // Only add https:// if value has actual content and doesn't already have protocol
    else if (
      value &&
      value.trim() !== "" &&
      value !== "https://" &&
      value !== "http://" &&
      !value.startsWith("https://") &&
      !value.startsWith("http://")
    ) {
      value = "https://" + value;
    }

    const currentLinks = brandForms[brandIndex]?.social_links || [""];
    const updatedLinks = [...currentLinks];
    updatedLinks[linkIndex] = value;
    updateForm(brandIndex, { social_links: updatedLinks });
    clearFieldError(brandIndex, "social_links");
  };

  const handleRemoveLink = (brandIndex, linkIndex) => {
    const currentLinks = brandForms[brandIndex]?.social_links || [""];

    if (currentLinks.length === 1) {
      addToast({
        error: true,
        msg: "At least one social link input is required!",
      });
      return;
    }

    const updatedLinks = currentLinks.filter((_, idx) => idx !== linkIndex);
    updateForm(brandIndex, { social_links: updatedLinks });
  };

  const handleSetCroppedImage = (index) => (image) => {
    setCroppedImages((prev) => ({
      ...prev,
      [index]: image,
    }));
    clearFieldError(index, "logo_img");
  };

  const handleImageUploadComplete = (index, uploadedUrl) => {
    console.log("Image upload complete, URL:", uploadedUrl);

    setUploadedImageUrls((prev) => ({
      ...prev,
      [index]: uploadedUrl,
    }));

    setCroppedImages((prev) => ({
      ...prev,
      [index]: uploadedUrl,
    }));

    updateForm(index, {
      logo_img: uploadedUrl,
      logo_img_url: uploadedUrl,
    });

    clearFieldError(index, "logo_img");
  };

  useEffect(() => {
    try {
      let data = "";
      const userInfo = storageHandler.getUserInfo();
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        data = parsed?.data || null;
      }
      setIsUserHasSubscription(data?.isUserHasSubscription);
    } catch (error) {
      console.error("Error parsing user info:", error);
    }
  }, []);

  return (
    <>
      {isUserHasSubscription ? (
        <>
          <div>
            {isLoading ? (
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                 
                  <p className="text-gray-600">Loading your brand profiles...</p>
                </div>
              </div>
            ) : brandprofileData.length > 0 ? (
              <>
                {brandprofileData.length > 0 && (
                  <div className="flex justify-end items-center mb-6">
                    <button
                      onClick={handleAddNew}
                      className="px-6 py-3 bg-emerald-500 text-white rounded-2xl hover:bg-emerald-600 transition-colors duration-300 font-semibold flex items-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add New Profile
                    </button>
                  </div>
                )}

                {/* Render existing profiles */}
                {brandprofileData.map((data, index) => {
                  const form = brandForms[index] || {};
                  const editorState = editorStates[index];
                  const selectedCountry = getSelectedCountry(index);
                  const imageSrc = getImageSrc(index);

                  return (
                    <div
                      className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl shadow-emerald-100/50 border border-emerald-100 overflow-hidden mb-5 transition-all duration-300"
                      key={`brand-${data.id || index}`}
                    >
                      {/* Accordion Header */}
                      <div
                        className="p-6 cursor-pointer hover:bg-emerald-50/50 transition-colors"
                        onClick={() => toggleAccordion(index)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center overflow-hidden">
                              {imageSrc ? (
                                <img
                                  src={imageSrc}
                                  alt="Brand logo"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `${process.env.PUBLIC_URL}/images/placeholder/avatar.png`;
                                  }}
                                />
                              ) : (
                                <span className="text-emerald-600 font-bold text-lg">
                                  {form.brand_name?.charAt(0) ||
                                    data.brand_name?.charAt(0) ||
                                    "B"}
                                </span>
                              )}
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-emerald-900">
                                {form.brand_name ||
                                  data.brand_name ||
                                  "Unnamed Brand"}
                              </h3>
                              <p className="text-sm text-emerald-600">
                                {form.industry ||
                                  data.industry ||
                                  "No industry specified"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {openAccordion !== index &&
                              (isSingleProfile() ||
                                form.primary === 1 ||
                                primaryBrands[index]) && (
                                <span className="text-sm font-semibold text-gray-400 italic px-3 py-1 bg-grey-50 rounded-full">
                                  Primary
                                </span>
                              )}
                            {openAccordion === index && (
                              <div
                                className="bg-gray-50/80 backdrop-blur-sm border border-gray-100 rounded-3xl px-4 py-2 flex items-center gap-1 h-12 hover:bg-gray-100/80 transition-all"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <SwitchWithLabel
                                  id={`primary-${index}`}
                                  labelRight=""
                                  className="scale-75"
                                  checked={
                                    isSingleProfile()
                                      ? true
                                      : primaryBrands[index] || false
                                  }
                                  disabled={isSingleProfile()}
                                  onCheckedChange={(checked) => {
                                    if (!isSingleProfile()) {
                                      setPrimaryBrands((prev) => ({
                                        ...prev,
                                        [index]: checked,
                                      }));
                                    }
                                  }}
                                />
                                <span
                                  className={`text-sm font-semibold ${
                                    isSingleProfile()
                                      ? "text-gray-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  Primary
                                </span>
                              </div>
                            )}
                            <svg
                              className={`w-5 h-5 text-emerald-500 transform transition-transform ${
                                openAccordion === index ? "rotate-180" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Accordion Content */}
                      <div
                        className={`transition-all duration-300 overflow-hidden ${
                          openAccordion === index
                            ? "max-h-[2000px] opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="p-8 border-t border-emerald-100">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Brand Name */}
                            <div className="space-y-3">
                              <label className="block text-sm font-semibold text-emerald-900">
                                Brand name{" "}
                                <span className="text-red-400">*</span>
                              </label>
                              <div className="relative group">
                                <input
                                  value={form.brand_name || ""}
                                  type="text"
                                  onChange={setInput(index, "brand_name")}
                                  tabIndex={0}
                                  className={`w-full p-4 border rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors resize-vertical text-gray-900 placeholder-gray-400 ${
                                    getFieldError(index, "brand_name")
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                  placeholder="Enter your brand name"
                                />
                              </div>
                              {getFieldError(index, "brand_name") && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                                  {getFieldError(index, "brand_name")}
                                </p>
                              )}
                            </div>

                            {/* Website */}
                            <div className="space-y-3">
                              <label className="block text-sm font-semibold text-emerald-900">
                                Website<span className="text-red-400">*</span>
                              </label>
                              <div className="relative group">
                                <input
                                  value={form.website || ""}
                                  type="text"
                                  onChange={setInput(index, "website")}
                                  tabIndex={0}
                                  className={`w-full p-4 border rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors resize-vertical text-gray-900 placeholder-gray-400 ${
                                    getFieldError(index, "website")
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                  placeholder="Enter your website URL"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                                  <svg
                                    className="w-5 h-5 text-emerald-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                                    />
                                  </svg>
                                </div>
                              </div>
                              {getFieldError(index, "website") && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                                  {getFieldError(index, "website")}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-5">
                            {/* Industry */}
                            <div className="space-y-3">
                              <label className="block text-sm font-semibold text-emerald-900">
                                Industry <span className="text-red-400">*</span>
                              </label>
                              <div className="relative group">
                                <input
                                  value={form.industry || ""}
                                  type="text"
                                  onChange={setInput(index, "industry")}
                                  tabIndex={0}
                                  className={`w-full p-4 border rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors resize-vertical text-gray-900 placeholder-gray-400 ${
                                    getFieldError(index, "industry")
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                  placeholder="Enter your industry name"
                                />
                              </div>
                              {getFieldError(index, "industry") && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                                  {getFieldError(index, "industry")}
                                </p>
                              )}
                            </div>

                            {/* Country */}
                            <div className="space-y-3">
                              <label className="block text-sm font-semibold text-emerald-900">
                                Country <span className="text-red-400">*</span>
                              </label>
                              <SelectInput
                                value={String(form.country_id || "")}
                                list={selectCountryList}
                                selected={selectedCountry}
                                onChange={(value) => {
                                  updateForm(index, { country_id: value });
                                  clearFieldError(index, "country_id");
                                }}
                                error={!!getFieldError(index, "country_id")}
                                isRequired
                                tabIndex={0}
                                className={`w-full p-4 border rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors resize-vertical text-gray-900 placeholder-gray-400 ${
                                  getFieldError(index, "country_id")
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-300"
                                }`}
                              />
                              {getFieldError(index, "country_id") && (
                                <p className="text-red-500 text-sm flex !mt-0 items-center gap-2">
                                  {getFieldError(index, "country_id")}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-5">
                            {/* Profile Photo */}
                            <div className="space-y-3">
                              <label className="block text-sm font-semibold text-emerald-900">
                                Profile photo{" "}
                                <span className="text-red-400">*</span>
                              </label>
                              <div key={uploaderInstanceKeys[index]}>
                                <BrandProfileUploader
                                  brandId={data.id || index}
                                  form={{
                                    uploaded_profile_photo: imageSrc || "",
                                    profile_img: imageSrc || "",
                                    profile_img_url: imageSrc || "",
                                  }}
                                  croppedImage={croppedImages[index] || imageSrc}
                                  setCroppedImage={handleSetCroppedImage(index)}
                                  onUploadComplete={(uploadedUrl) => {
                                    handleImageUploadComplete(index, uploadedUrl);
                                  }}
                                  setShowConfirmInfModal={() => {}}
                                />
                              </div>
                              {getFieldError(index, "logo_img") && (
                                <div className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-200">
                                  {getFieldError(index, "logo_img")}
                                </div>
                              )}
                            </div>

                            {/* Social Links */}
                            <div>
                              <label className="text-emerald-900 text-sm font-semibold flex items-center mb-3">
                                Social Links{" "}
                                <span className="text-red-400">*</span>
                              </label>
                              {(form.social_links || [""]).map(
                                (link, linkIndex) => (
                                  <div
                                    key={linkIndex}
                                    className="relative group flex gap-2 mb-2"
                                  >
                                    <input
                                      type="text"
                                      value={link}
                                      onChange={(e) =>
                                        handleLinkChange(
                                          index,
                                          linkIndex,
                                          e.target.value
                                        )
                                      }
                                      tabIndex={0}
                                      className={`w-full p-4 border rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors resize-vertical text-gray-900 placeholder-gray-400 ${
                                        getFieldError(index, "social_links")
                                          ? "border-red-500 bg-red-50"
                                          : "border-gray-300"
                                      }`}
                                      placeholder={`Enter social link ${
                                        linkIndex + 1
                                      }`}
                                    />

                                    <div className="flex gap-2">
                                      {(form.social_links || [""]).length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRemoveLink(index, linkIndex)
                                          }
                                          className="mt-3 w-8 h-8 p-1 flex items-center justify-center border border-red-100 rounded-full bg-red-50 hover:bg-red-100 transition"
                                          title="Remove link"
                                        >
                                          <svg
                                            className="w-5 h-5 text-red-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M6 18L18 6M6 6l12 12"
                                            />
                                          </svg>
                                        </button>
                                      )}

                                      {linkIndex ===
                                        (form.social_links || [""]).length - 1 &&
                                        (form.social_links || [""]).length <
                                          4 && (
                                          <button
                                            type="button"
                                            onClick={() => handleAddLink(index)}
                                            className="mt-3 w-8 h-8 p-1 flex items-center justify-center border border-emerald-100 rounded-full bg-emerald-50 hover:bg-emerald-100 transition"
                                            title="Add link"
                                          >
                                            <Plus className="w-6 h-6 text-emerald-600" />
                                          </button>
                                        )}
                                    </div>
                                  </div>
                                )
                              )}

                              {getFieldError(index, "social_links") && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                                  {getFieldError(index, "social_links")}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Bio */}
                          <div className="bg-green-25 mt-5">
                            <label className="block text-sm font-semibold text-emerald-900 mb-3">
                              Bio <span className="text-red-500">*</span>
                            </label>
                            <div
                              className={`w-full p-4 border rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors overflow-hidden text-gray-900 placeholder-gray-400 ${
                                getFieldError(index, "bio")
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                            >
                              <EditorInput
                                form={form}
                                editorState={editorState}
                                onEditorStateChange={(state) =>
                                  updateTextDescription(index, state)
                                }
                                isRequired
                                tabIndex={0}
                              />
                            </div>
                            {getFieldError(index, "bio") && (
                              <p className="text-red-500 text-sm mt-2">
                                {getFieldError(index, "bio")}
                              </p>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-emerald-100">
                            {data.id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBrandToDelete({ index, data });
                                  setModal("delete");
                                }}
                                className="flex h-[42px] px-6 items-center gap-2 rounded-full text-sm font-semibold cursor-pointer border-none text-white transition-all duration-200 hover:opacity-90"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                                  boxShadow:
                                    "0px 2px 6px 0px rgba(239, 68, 68, 0.25)",
                                }}
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                                Delete
                              </button>
                            )}
                            <button
                              onClick={() => handleSave(index, data)}
                              tabIndex={0}
                              className="flex h-[42px] px-6 items-center gap-2 rounded-full text-sm font-semibold cursor-pointer border-none text-white transition-all duration-200 hover:opacity-90"
                              style={{
                                background:
                                  "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                                boxShadow:
                                  "0px 2px 6px 0px rgba(16, 185, 129, 0.25)",
                              }}
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              {data.id ? "Update Changes" : "Save Changes"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {modal === "delete" && (
                  <ModelCommon
                    close={() => setModal(null)}
                    buttonText="Delete"
                    setModal={setModal}
                    title="Delete Profile"
                    titleClassname="text-red-500"
                    description="Are you sure you want to delete this profile?"
                    onConfirm={(setIsLoading) => {
                      if (brandToDelete) {
                        handleDelete(
                          brandToDelete.index,
                          brandToDelete.data,
                          true,
                          setIsLoading
                        );
                      }
                    }}
                    className="bg-gradient-to-r from-red-50 to-red-100"
                    buttonClassName="bg-gradient-to-r from-red-500 to-red-700 focus:ring-red-500"
                    icon={<CircleX color="#ef4444" strokeWidth={2.25} size={24} />}
                  />
                )}
              </>
            ) : (
              // Show "Create Your First Brand Profile" only when not loading and no profiles exist
              <div className="flex flex-col items-center justify-center min-h-[80vh] text-center">
                <div className="max-w-md mx-auto p-8">
                  <div className="w-24 h-24 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-12 h-12 text-emerald-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-emerald-900 mb-3">
                    Create Your First Brand Profile
                  </h2>
                  <p className="text-gray-600 mb-8">
                    You don't have any brand profiles yet. Create your first
                    brand profile to get started with managing your brand's
                    online presence.
                  </p>
                  <button
                    onClick={handleAddNew}
                    className="px-8 py-4 bg-emerald-500 text-white rounded-2xl hover:bg-emerald-600 transition-colors duration-300 font-semibold flex items-center gap-3 mx-auto"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Create New Profile
                  </button>
                </div>
              </div>
            )}
          </div>
          {toastList && <Toast list={toastList} setList={setToastList} />}
        </>
      ) : (
        <>
          <NotSubscribe
            goToPricing={props.goToPricing}
            type="brand"
            setAuth={props.setAuth}
            setModal={setModal}
            NotifModul={props.NotifModul}
            setNotifModul={props.setModalNotification}
            profiles={props.profiles}
            closeCartPanel={props.closeCartPanel}
            user={props.user}
            cartItem={props.cartItem}
            title={"List"}
            background={"background_inflist.png"}
          ></NotSubscribe>
        </>
      )}
    </>
  );
}